import React, { useState, useEffect, useRef } from 'react';
import './SideBar.style.scss';
import { usePlayer } from '../../../Contexts/playerData.context.jsx';
import FBXButtonVertical from '../../NavItems/FBXVertical/FBXButtonVertical.component';
import userWhite from '../../../assets/icons/user_white.svg';
import calendarIcon from '../../../assets/icons/calendar.svg';
import expand from '../../../assets/icons/expand.svg';
import { useNavigate } from 'react-router-dom';
import bug from '../../../assets/icons/bug-white.svg';
import { fetchData, getUserFromToken, trackUsageEvent } from '../../../utils/functions.ts';
import { useNavBar } from '../../../Contexts/navbar.items.context.jsx';
import logo from '../../../assets/Logo/FBX_2024_dark.svg';
import { useDataLayer } from '../../../Contexts/DataLayer.context.jsx';
import { useBoxContext } from '../../../Contexts/boxData.context.jsx';

const SHARED_NOTE_CHANGED_EVENT = "FBX_SHARED_NOTE_CHANGED_COUNT";
const NOTES_ENDPOINT = "https://www.fbxchange.com/ReactPHP/notes.php";
const SHARED_NOTE_CHANGED_POLL_MS = 5 * 60 * 1000;

const hasAdminAccess = (user) => {
    return user?.userContactLevel === '1';
};

const SideBar = ({ isShrunk }) => {

    const { setPlayerData, setListLoadID, setSavedOriginalPlayerData, originalPlayerData, setPlayerID, setActivePlayer } = usePlayer();
    const [isShrunkValue, setShrunk] = useState(isShrunk);
    const { updateSearchedListName, activeSideButton, updateActiveSideButton } = useNavBar();

    // ✅ pull blinkSQ + reset from DataLayer
    const { updateWhichOneLiner, blinkSQ, resetBlinkSQ } = useDataLayer();

    const { setNoteBoxOpen } = useBoxContext();
    const navigate = useNavigate(); // Initialize navigate

    const [showFields, setShowFields] = useState({});
    const [hasAdmin, setHasAdmin] = useState(false);
    const [sharedNoteChangedCount, setSharedNoteChangedCount] = useState(0);
    const [sharedNoteAttentionDismissed, setSharedNoteAttentionDismissed] = useState(false);
    const sharedNoteChangedCountRef = useRef(0);

    const applySharedNoteChangedCount = (nextCount) => {
        const cleanCount = Number.parseInt(nextCount || 0, 10) || 0;
        setSharedNoteChangedCount(cleanCount);
        if (cleanCount <= 0) {
            setSharedNoteAttentionDismissed(false);
        } else if (cleanCount > sharedNoteChangedCountRef.current) {
            setSharedNoteAttentionDismissed(false);
        }
        sharedNoteChangedCountRef.current = cleanCount;
    };

    const loadSharedNoteChangedCount = async (userIn = null) => {
        const user = userIn || await getUserFromToken();
        if (!user?.team || !user?.scout) {
            setSharedNoteChangedCount(0);
            return;
        }

        try {
            const response = await fetch(NOTES_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    scriptName: 'getSharedTeamNoteChangedSummary',
                    team: user.team,
                    scout: user.scout,
                }),
            });
            const data = await response.json();
            if (data?.success) {
                applySharedNoteChangedCount(data.changedCount);
            }
        } catch {}
    };

    useEffect(() => {
        let isMounted = true;

        const loadShowFields = async () => {
            const user = await getUserFromToken();
            if (!isMounted) return;
            setHasAdmin(hasAdminAccess(user));
            setShowFields(user?.showFields || {});
            loadSharedNoteChangedCount(user);
        };

        loadShowFields();

        const handleFocus = () => loadShowFields();
        const handleSharedNoteChanged = (event) => {
            applySharedNoteChangedCount(event?.detail?.changedCount);
        };
        window.addEventListener('focus', handleFocus);
        window.addEventListener(SHARED_NOTE_CHANGED_EVENT, handleSharedNoteChanged);
        const sharedNotePollId = window.setInterval(() => {
            loadSharedNoteChangedCount();
        }, SHARED_NOTE_CHANGED_POLL_MS);

        return () => {
            isMounted = false;
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener(SHARED_NOTE_CHANGED_EVENT, handleSharedNoteChanged);
            window.clearInterval(sharedNotePollId);
        };
    }, []);

    const isShowFieldNo = (field) => String(showFields?.[field] || '').toLowerCase() === 'no';

    // --------------------------------------------
    // SQ Blink (purple <-> orange) 3 times
    // --------------------------------------------
    const [sqFlashColor, setSqFlashColor] = useState(null);
    const sqBlinkTimeoutsRef = useRef([]);

    const clearSqBlink = () => {
        sqBlinkTimeoutsRef.current.forEach((t) => clearTimeout(t));
        sqBlinkTimeoutsRef.current = [];
        setSqFlashColor(null);
    };

    const runBlinkSQ = () => {
        clearSqBlink();

        const steps = ['orange', 'var(--purple)', 'orange', 'var(--purple)', 'orange', 'var(--purple)'];
        const stepMs = 400; // slower (you set this)

        steps.forEach((color, i) => {
            const tid = setTimeout(() => setSqFlashColor(color), i * stepMs);
            sqBlinkTimeoutsRef.current.push(tid);
        });

        const doneId = setTimeout(() => setSqFlashColor(null), steps.length * stepMs + 20);
        sqBlinkTimeoutsRef.current.push(doneId);
    };

    useEffect(() => {
        return () => clearSqBlink();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ NEW: watch the context trigger
    useEffect(() => {
        if (!blinkSQ) return;

        runBlinkSQ();

        // reset trigger back to false so next "true" will fire again
        resetBlinkSQ();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blinkSQ]);
    // --------------------------------------------

    const expandBox = () => {
        if (isShrunkValue) {
            setShrunk(false);
            updateActiveSideButton(null); // Open the clicked box
        } else {
            setShrunk(true);
        }
    };

    const processSideButton = async (event, buttonIdentifier) => {
        const labels = {
            S1: "S1 Opened",
            S2: "S2 Opened",
            S3: "S3 Last Search Opened",
            L1: "L1 Lists Opened",
            L2: "L2 Shared Lists Opened",
            SQ: "SQ Saved Query Opened",
            A: "A Alerts Opened",
            SN: "SN Shared Notes Opened",
            Blue: "BluBars Opened",
            user: "Purple Man Opened",
            CAL: "Calendar Opened",
            legend: "LG Legend Opened",
            Data: "Data Admin Panel Opened",
            Bug: "Bug Report Opened",
        };
        trackUsageEvent(labels[buttonIdentifier] || `${buttonIdentifier} Opened`, {
            source: "sidebar",
            metadata: { buttonId: buttonIdentifier },
        });
        setNoteBoxOpen(false);
        setShrunk(true);
        if (buttonIdentifier === 'SN') {
            setSharedNoteAttentionDismissed(true);
        }

        // ✅ removed: blink from S1 click

        if (buttonIdentifier === activeSideButton) {
            updateActiveSideButton(null); // Close the currently active box
        } else {
            updateActiveSideButton(buttonIdentifier); // Open the clicked box
        }

        processTimeLeft();

        if (buttonIdentifier === 'S3' && buttonIdentifier !== activeSideButton) { // Only fetch data if opening the box
            try {
                updateWhichOneLiner(null);
                const user = await getUserFromToken();
                if (!user) return;
                setSavedOriginalPlayerData([]);
                const newPlayers = await fetchData('Last List', user.scout, user.team);
                console.log('Fetched Last List:', newPlayers[0]);
                switch (newPlayers[0]['searchType']) {
                    case 'Scout':
                        updateWhichOneLiner('Scout');
                        break;
                    case 'Note':
                        updateWhichOneLiner('Note');
                        break;
                    default:
                        updateWhichOneLiner(null);
                }
                newPlayers.sort((a, b) => {
                    const lastCompare = (a.last || '').localeCompare(b.last || '');
                    return lastCompare !== 0 ? lastCompare : (a.first || '').localeCompare(b.first || '');
                });
                setPlayerData(newPlayers);
                setSavedOriginalPlayerData(newPlayers);
                setPlayerID(newPlayers[0]?.FBXID || '');
                setActivePlayer(newPlayers[0] || []);
                updateSearchedListName('Last List Search');
                setListLoadID(Math.floor(Math.random() * (1000000 - 1000 + 1)) + 1000); // Generate Random Number to tell the system a new list is sent
            } catch (error) {
                return;
            }
        }
    };

    const processTimeLeft = async () => {

        const user = await getUserFromToken();
        if (!user) return;
        const threshold = user.threshold;
        const now = Math.floor(Date.now() / 1000);
        const timeLeft = threshold - now;
        if (timeLeft < 0) {
            alert("You will be signed out in 10 minutes. Go to Your Account to Extend Your Time.");
            // try {
            //     const response = await fetch('https://www.fbxchange.com/ReactPHP/purpleman.php', {
            //         method: 'POST',
            //         headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            //         body: new URLSearchParams({
            //             method: 'threshold',
            //             user: user.scout,
            //             theValue: user,
            //         }).toString(),
            //     });
            //     const data = await response.json();
            //     if (data.success && data.user) {
            //         saveAuthUser(data.user);
            //     }
            // } catch (error) {
            //     return;
            // }
        }
    };

    return (
        <div className={`sidebar-wrapper ${isShrunkValue ? 'shrunk' : 'expand'}`}>
            <div style={{ color: "white" }}>
                <img style={{ marginTop: "7px" }} src={logo} alt={'None' || 'Icon'} width="48" height="48" />
            </div>

            <div className="topBox w-100">
                {!isShowFieldNo('showS1') && (
                    <FBXButtonVertical onClick={processSideButton} isSelected={activeSideButton === 'S1'} IconText='S1' buttonColor="var(--success)" buttonID="S1" textColor="var(--white)" theTooltip="Search" hasIcon={false} expandedText="Regular Search" expanded={isShrunkValue} marginTop="2px" />
                )}
                {!isShowFieldNo('showS2') && (
                    <FBXButtonVertical onClick={processSideButton} isSelected={activeSideButton === 'S2'} IconText='S2' buttonColor="var(--success)" buttonID="S2" textColor="var(--white)" theTooltip="Advanced Search" hasIcon={false} expandedText="Advanced Search" expanded={isShrunkValue} />
                )}
                {!isShowFieldNo('showS3') && (
                    <FBXButtonVertical onClick={processSideButton} isSelected={activeSideButton === 'S3'} IconText='S3' buttonColor="var(--success)" buttonID="S3" textColor="var(--white)" theTooltip="Last Search" hasIcon={false} expandedText="Last Search" expanded={isShrunkValue} />
                )}
                {!isShowFieldNo('showLIST') && (
                    <FBXButtonVertical onClick={processSideButton} isSelected={activeSideButton === 'L1'} IconText='L1' buttonColor="var(--purple)" selectedColor="var(--purple)" hoverColor="var(--purple)" buttonID="L1" textColor="var(--white)" theTooltip="List" hasIcon={false} expandedText="Lists" expanded={isShrunkValue} />
                )}
                {!isShowFieldNo('showSHARED') && (
                    <FBXButtonVertical onClick={processSideButton} isSelected={activeSideButton === 'L2'} IconText='L2' buttonColor="var(--purple)" selectedColor="var(--purple)" hoverColor="var(--purple)" buttonID="L2" textColor="var(--white)" theTooltip="Shared List" hasIcon={false} expandedText="Shared Lists" expanded={isShrunkValue} />
                )}

                {/* ✅ SQ button color is overridden while blinking */}
                <FBXButtonVertical
                    onClick={processSideButton}
                    isSelected={activeSideButton === 'SQ'}
                    IconText='SQ'
                    buttonColor={sqFlashColor ?? "var(--purple)"}
                    selectedColor="var(--purple)"
                    hoverColor="var(--purple)"
                    buttonID="SQ"
                    textColor="var(--white)"
                    theTooltip="Saved Query"
                    hasIcon={false}
                    expandedText="Saved Query"
                    expanded={isShrunkValue}
                />

                <FBXButtonVertical onClick={processSideButton} isSelected={activeSideButton === 'A'} IconText='A' buttonColor="var(--blue)" buttonID="A" textColor="var(--white)" theTooltip="Alerts" hasIcon={false} expandedText="Alert List" expanded={isShrunkValue} />
                <FBXButtonVertical onClick={processSideButton} isSelected={activeSideButton === 'SN'} IconText='SN' buttonColor="var(--brown)" buttonID="SN" textColor="var(--white)" theTooltip="Shared Notes" hasIcon={false} expandedText="Shared Notes" expanded={isShrunkValue} badgeCount={sharedNoteChangedCount} attention={sharedNoteChangedCount > 0 && !sharedNoteAttentionDismissed && activeSideButton !== 'SN'} />
                {hasAdmin && (
                    <FBXButtonVertical isSelected={activeSideButton === 'Data'} onClick={processSideButton} buttonID="Data" theTooltip="Data" hasIcon={false} expandedText="Data" expanded={isShrunkValue} IconText="D" textColor="#fff" buttonColor="var(--primary)" />
                )}
                {hasAdmin && (
                    <FBXButtonVertical onClick={processSideButton} isSelected={activeSideButton === 'Blue'} IconText='BB' buttonColor="var(--blue)" buttonID="Blue" textColor="var(--white)" theTooltip="BluBars" hasIcon={false} expandedText="BluBars" expanded={isShrunkValue} />
                )}
            </div>

            <div className="bottomBox w-100">
                <FBXButtonVertical isSelected={activeSideButton === 'user'} onClick={processSideButton} buttonID="user" theTooltip="Your Account" hasIcon={true} expandedText="Your Account" expanded={isShrunkValue} buttonIcon={userWhite} />
                <FBXButtonVertical isSelected={activeSideButton === 'CAL'} onClick={processSideButton} buttonID="CAL" theTooltip="Event Calendar" hasIcon={true} expandedText="Event Calendar" expanded={isShrunkValue} buttonIcon={calendarIcon} />
                <FBXButtonVertical isSelected={activeSideButton === 'legend'} onClick={processSideButton} buttonID="legend" theTooltip="Legend" hasIcon={false} expandedText="Legend" expanded={isShrunkValue} IconText="LG" textColor="#ccc" />
                <FBXButtonVertical isSelected={activeSideButton === 'Bug'} onClick={processSideButton} buttonID="Bug" theTooltip="Bug Report" hasIcon={true} expandedText="Send Bug Report" expanded={isShrunkValue} buttonType="icon" buttonIcon={bug} />
                <FBXButtonVertical onClick={expandBox} buttonID="shrink" theTooltip="Shrink/Expand" hasIcon={true} expandedText="Shrink Sidebar" expanded={isShrunkValue} buttonType="icon" buttonIcon={expand} />
            </div>
        </div>
    );
};

export default SideBar;
